/**
 * The instance settings, fetched once per signed-in session.
 *
 * About forty numbers that used to be compile-time constants in `JetsonCrm.Contracts` and are now a
 * document an administrator edits: transfer caps, listing caps, event-log caps, retention. Each
 * process holds its own copy - the server loads it at startup, agents are handed it over the hub, and
 * a browser front end fetches it after sign-in - so **a client that bakes in a build-time copy is a
 * client that clamps to a number the deployment no longer uses.** That is why `contracts.gen.ts`
 * carries only the `const` members and not the `*Limits` properties.
 *
 * It is loaded once and read synchronously, because it is read from inside `onPaint`-adjacent code
 * and from sentences built while a page lays itself out - an async read at each of those sites would
 * be an await in a code path that cannot have one. `DEFAULTS` is what those reads see before the
 * fetch lands, and they are the values the product shipped with, so the worst case is a sentence
 * naming yesterday's cap for a moment rather than a page that cannot render.
 *
 * The endpoint is STAFF, not admin: "that transfer was too large" is only actionable next to the
 * number, and the knowledge base gets its own caps a different way for exactly that reason.
 */
import type { AppSettingsDto, AppSettingsResponse } from '../contracts.gen.js';
import { request } from './http.js';

/**
 * The values the product shipped with.
 *
 * Only the sections this app reads outside the Settings page are filled in; the rest arrive with the
 * fetch. A section missing here is a section nothing renders a number from yet.
 */
const DEFAULTS = {
  remoteFiles: { maxTotalBytes: 200 * 1024 * 1024, maxFiles: 20, stagingTtlMinutes: 15 },
  fileExplorer: { maxEntries: 1000, browseTimeoutSeconds: 20, transferTimeoutSeconds: 300 },
  eventLog: { maxEvents: 200, maxScan: 5000, maxMessageChars: 4000, queryTimeoutSeconds: 30 }
} as const;

let current: AppSettingsDto | null = null;
let loading: Promise<void> | null = null;

/**
 * Fetches the settings, once.
 *
 * Never throws: a failed read leaves the defaults in force, and refusing to render a device page
 * because a cap could not be read would be the wrong trade by a distance.
 */
export function load(): Promise<void> {
  if (loading) return loading;
  loading = (async () => {
    try {
      const response = await request<AppSettingsResponse>('/api/settings');
      current = response.settings;
    } catch {
      current = null;
    }
  })();
  return loading;
}

/**
 * Takes the values a save or a reset answered with, so the caps this browser quotes move with the
 * Settings page rather than waiting for the next sign-in.
 */
export function adopt(settings: AppSettingsDto): void {
  current = settings;
  loading = Promise.resolve();
}

/** Dropped on sign-out, so the next session's settings are that deployment's rather than this one's. */
export function reset(): void {
  current = null;
  loading = null;
}

/** The most entries one folder listing carries before the server caps it. */
export function maxFileEntries(): number {
  return current?.fileExplorer?.maxEntries ?? DEFAULTS.fileExplorer.maxEntries;
}

/** The most events one event-log query returns. */
export function maxEvents(): number {
  return current?.eventLog?.maxEvents ?? DEFAULTS.eventLog.maxEvents;
}

/** How many records a text search scans on the device before it gives up. */
export function maxEventScan(): number {
  return current?.eventLog?.maxScan ?? DEFAULTS.eventLog.maxScan;
}

/**
 * The transfer cap, as a sentence.
 *
 * **0 MEANS UNLIMITED**, and that reading has to live somewhere the UI can reach: the stored value
 * is 0 rather than a huge number precisely so the two states are distinguishable, and a page that
 * printed "0 MB / 0 files" would be describing the opposite of what is configured.
 */
export function describeTransferCap(): string {
  const cap = current?.remoteFiles ?? DEFAULTS.remoteFiles;
  const size =
    cap.maxTotalBytes > 0 ? `${Math.round(cap.maxTotalBytes / (1024 * 1024))} MB` : 'no size limit';
  const count = cap.maxFiles > 0 ? `${cap.maxFiles} files` : 'any number of files';
  return `${size} / ${count} per transfer`;
}
